import React, { useMemo } from 'react';
import {
  CardNumberElement,
  CardCvcElement,
  CardExpiryElement,
} from '@stripe/react-stripe-js';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import EventIcon from '@mui/icons-material/Event';
import NumbersIcon from '@mui/icons-material/Numbers';
import { Box, Card, CardContent, Grid, Typography } from '@mui/material';
import { useFormikContext } from 'formik';
import { PaymentMethodForm } from 'renderer/features/settings/schemas/paymentMethods.schemas';
import useResponsiveFontSize from './useResponsiveFontSize';

const useOptions = (disabled: boolean) => {
  const fontSize = useResponsiveFontSize();
  const options: any = useMemo(
    () => ({
      disabled,
      style: {
        base: {
          fontSize: 14,
          fontFamily: 'Arial, sans-serif',
          '::placeholder': {},
        },
        invalid: {
          color: '#9e2146',
        },
      },
    }),
    [disabled],
  );

  return options;
};

interface StripeCreditCardFormProps {
  disabled?: boolean;
}

const StripeCreditCardForm: React.FC<StripeCreditCardFormProps> = ({
  disabled,
}) => {
  const options = useOptions(!!disabled);
  const { errors, touched, setFieldValue, setFieldTouched } =
    useFormikContext<PaymentMethodForm>();

  return (
    <Card elevation={0}>
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1">Card Number:</Typography>
            <Typography variant="body2">Enter your card number</Typography>
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <CreditCardIcon sx={{ marginRight: '10px' }} />
            <Box
              sx={{
                border: `1px solid ${
                  touched.numberValidity && errors.numberValidity
                    ? 'red'
                    : 'black'
                }`,
                borderRadius: '4px',
                flexGrow: 1,
                p: 1,
              }}
            >
              <CardNumberElement
                options={options}
                onReady={() => {
                  setFieldValue('numberValidity', '');
                }}
                onChange={(e) => {
                  if (e.empty) setFieldValue('numberValidity', '');
                  else
                    setFieldValue('numberValidity', e.complete ? 'valid' : '');
                }}
                onBlur={() => setFieldTouched('numberValidity', true, false)}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1">Expiry Date:</Typography>
            <Typography variant="body2">
              Enter the expiration on the card
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <EventIcon sx={{ marginRight: '10px' }} />
            <Box
              sx={{
                border: `1px solid ${
                  touched.expirationDateValidity &&
                  errors.expirationDateValidity
                    ? 'red'
                    : 'black'
                }`,
                borderRadius: '4px',
                flexGrow: 1,
                p: 1,
              }}
            >
              <CardExpiryElement
                options={options}
                onReady={() => setFieldValue('expirationDateValidity', '')}
                onChange={(e) => {
                  if (e.empty) setFieldValue('expirationDateValidity', '');
                  else
                    setFieldValue(
                      'expirationDateValidity',
                      e.complete ? 'valid' : '',
                    );
                }}
                onBlur={() =>
                  setFieldTouched('expirationDateValidity', true, false)
                }
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1">CVC Number:</Typography>
            <Typography variant="body2">
              Enter the 3 or 4 digit number
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <NumbersIcon sx={{ marginRight: '10px' }} />
            <Box
              sx={{
                border: `1px solid ${
                  touched.cvvValidity && errors.cvvValidity ? 'red' : 'black'
                }`,
                borderRadius: '4px',
                flexGrow: 1,
                p: 1,
              }}
            >
              <CardCvcElement
                options={{ ...options, placeholder: '123' }}
                onReady={() => setFieldValue('cvvValidity', '')}
                onChange={(e) => {
                  if (e.empty) setFieldValue('cvvValidity', '');
                  else setFieldValue('cvvValidity', e.complete ? 'valid' : '');
                }}
                onBlur={() => setFieldTouched('cvvValidity', true, false)}
              />
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default StripeCreditCardForm;
